import { useQuery } from "@tanstack/vue-query";
import { useAuth } from "~/store/auth";
import {
	IGamingExclusion,
	ILimit,
	IResponsibleGamingReasonsResponse,
	IResponsibleGamingLimitResponse,
} from "~/types/responsible-gaming/responsible-gaming";
import { useSettings } from "~/store/settings";
import { IFeature, IResponsibleGamingSetting } from "~/types/kentico/app-settings";
import api from "~/utils/auth/api";

export const isLimitsInitiated = ref(false);
export const isExclInitiated = ref(false);

const enabledLimits = () => {
	const settings = useSettings();
	const enabled = [];

	if (settings.hasFeature("IsLossLimitEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("LOSSLIMIT");
	}

	if (settings.hasFeature("IsDepositLimitEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("DEPOSITLIMIT");
	}

	if (settings.hasFeature("IsWagerLimitEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("WAGERLIMIT");
	}

	return enabled;
};

const enabledExcl = () => {
	const settings = useSettings();
	const enabled: ("SESSIONLIMIT" | "COOLOFF" | "SELFEXCLUSION")[] = [];

	if (settings.hasFeature("IsSessionLimitEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("SESSIONLIMIT");
	}

	if (settings.hasFeature("IsSelfExclusionEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("SELFEXCLUSION");
	}

	if (settings.hasFeature("IsCoolingOffPeriodEnabled", "ResponsibleGamingSwitches")) {
		enabled.push("COOLOFF");
	}

	return enabled;
};

export const initResponsibleGamingLimits = async () => {
	const settings = useSettings();
	const auth = useAuth();
	if (!settings.hasFeature("IsResponsibleGamingEnabled", "AppSettings")) return;
	if (enabledLimits().length > 0) {
		const fetchLimitsData = enabledLimits() as Array<"DEPOSITLIMIT" | "WAGERLIMIT" | "LOSSLIMIT">;
		await auth.responsibleGamingLimits(fetchLimitsData);
	}

	isLimitsInitiated.value = true;
};

export const initResponsibleGamingExcl = async () => {
	const settings = useSettings();
	const auth = useAuth();
	if (!settings.hasFeature("IsResponsibleGamingEnabled", "AppSettings")) return;
	const fetchExclData = enabledExcl();
	if (fetchExclData.length > 0) {
		await auth.responsibleGamingExclusions(fetchExclData);
	}
	isExclInitiated.value = true;
};

export const findExclusion = (data: IGamingExclusion[] | undefined, exclusionKey: string) => {
	const auth = useAuth();
	const exclusionData = data?.find((data: IGamingExclusion) => data.exclusionType === exclusionKey && data.isActive);
	if (!exclusionData) {
		return {
			accountId: auth.profile?.id,
			editedById: auth.profile?.id,
			editedDateTime: new Date(),
			endDateTime: new Date(),
			exclusionType: exclusionKey,
			periodInterval: "MINUTE",
			periodValue: null,
			responsibleGamingExclusionReasonId: null,
			reason: null,
			version: 0,
			isActive: true,
			isDeleted: false,
			startDateTime: new Date(),
		} as IGamingExclusion;
	}

	return exclusionData;
};

export const getResponsibleGamingMessageCode = (responsibleGamingResponse: IResponsibleGamingLimitResponse | undefined, submitData: any) => {
	if (responsibleGamingResponse && responsibleGamingResponse.limitMessages) {
		if (submitData && responsibleGamingResponse.limitMessages[`${submitData.limitType}-${submitData.editedById}`]) {
			return responsibleGamingResponse.limitMessages[`${submitData.limitType}-${submitData.editedById}`].split("::")[0];
		} else if ("00000000-0000-0000-0000-000000000000" in responsibleGamingResponse.limitMessages) {
			return responsibleGamingResponse.limitMessages["00000000-0000-0000-0000-000000000000"].split("::")[0];
		} else if (responsibleGamingResponse.limitMessages[""]) {
			return responsibleGamingResponse.limitMessages[""].split("::")[0];
		} else if (responsibleGamingResponse.limitMessages[`${submitData.limitType}`]) {
			return responsibleGamingResponse.limitMessages[`${submitData.limitType}`].split("::")[0];
		}
	}
	return null;
};

export const sessionLimitDurationOptions = () => {
	const periodOptions = ["5", "15", "30", "60", "120", "240"];
	const settings = useSettings();
	const responsibleGamingSettings = settings.config?.appSetting?.ResponsibleGamingSettings;
	if (responsibleGamingSettings) {
		const setting = computed<IResponsibleGamingSetting | undefined>(() => {
			if (responsibleGamingSettings) {
				return responsibleGamingSettings.find((setting) => setting.Feature === "SessionLimit");
			}
			return undefined;
		});

		if (setting.value && setting.value.SessionTimerMaxLimit) {
			return periodOptions.filter((option) => parseInt(option) <= (setting?.value?.SessionTimerMaxLimit ?? 0));
		}
	}
};

export const exclusionReasons = (exclusionTypes: ("SESSIONLIMIT" | "COOLOFF" | "SELFEXCLUSION")[] = ["SELFEXCLUSION"]) => {
	return useQuery<IResponsibleGamingReasonsResponse>({
		queryKey: ["exclusion_reasons"],
		queryFn: () =>
			api({
				url: "/ResponsibleGaming/GetResponsibleGamingExclusionReasons",
				type: "player",
				options: { method: "POST" },
				data: { exclusionTypes },
			}),
	});
};
